// stores/schedule-store.js
import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestSchedule,
  deleteGuestSchedule,
  fetchGuestSchedules,
  isGuestMode,
  updateGuestSchedule,
} from "@/utils/guest-session";

const sanitizeValidationDetail = (detail) => {
  if (!Array.isArray(detail)) {
    return detail ?? null;
  }
  return detail.map(({ loc, msg, type }) => ({ loc, msg, type }));
};

export const useScheduleStore = defineStore("schedule", {
  state: () => ({
    schedules: [],
    isLoading: false,
    error: null,
  }),
  actions: {
    async fetchSchedules(shioriId, options = {}) {
      if (!options.silent) {
        this.isLoading = true;
        this.error = null;
      }
      try {
        if (isGuestMode()) {
          this.schedules = fetchGuestSchedules(shioriId);
          return this.schedules;
        }

        const response = await axios.get(
          `${API_URL}/schedule/?shiori_id=${shioriId}`,
        );
        this.schedules = response.data.map((schedule) => ({
          ...schedule,
          GOOGLE_PLACE_NMV:
            schedule.GOOGLE_PLACE_NMV ?? schedule.googlePlaceNmv ?? null,
          GOOGLE_PLACE_ID:
            schedule.GOOGLE_PLACE_ID ?? schedule.googlePlaceId ?? null,
          MEMBERS: (schedule.MEMBERS || []).map((member) => ({
            ...member,
            PROFILE_IMAGE_BASE64:
              member.PROFILE_IMAGE_BASE64 ||
              (member.HAS_PROFILE_IMAGE && member.APP_USER_CODE
                ? `${API_URL}/users/${member.APP_USER_CODE}/profile-image`
                : null),
          })),
        }));
      } catch (err) {
        if (!options.silent) {
          this.error = err;
        }
        console.error("スケジュール取得エラー:", err);
      } finally {
        if (!options.silent) {
          this.isLoading = false;
        }
      }
    },
    async createSchedule(scheduleData, shareToken = null) {
      this.isLoading = true;
      this.error = null;
      try {
        if (shareToken) {
          const response = await axios.post(
            `${API_URL}/shiori/shared/${shareToken}/schedule`,
            scheduleData,
          );
          return response.data;
        }
        if (isGuestMode()) {
          const result = createGuestSchedule(scheduleData);
          if (scheduleData.shioriId) {
            this.schedules = fetchGuestSchedules(scheduleData.shioriId);
          }
          return result;
        }

        const response = await axios.post(
          `${API_URL}/schedule/`,
          scheduleData,
          withShioriAccessContext(scheduleData.shioriId),
        );
        // 作成成功後、非共有の通常モードのみリストを再取得
        if (!shareToken && scheduleData.shioriId) {
          await this.fetchSchedules(scheduleData.shioriId);
        }
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("スケジュール作成エラー:", err);
        const validationDetail = sanitizeValidationDetail(
          err.response?.data?.detail,
        );
        console.error("スケジュール作成エラー詳細:", {
          status: err.response?.status ?? null,
          validationDetail,
          responseData: err.response?.data
            ? {
                ...err.response.data,
                detail: validationDetail,
              }
            : null,
          payloadKeys: Object.keys(scheduleData || {}),
        });
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async updateSchedule(shioriId, lineNon, scheduleData, options = {}, shareToken = null) {
      if (!options.silent) {
        this.isLoading = true;
        this.error = null;
      }
      try {
        if (shareToken) {
          const response = await axios.put(
            `${API_URL}/shiori/shared/${shareToken}/schedule/${lineNon}`,
            scheduleData,
          );
          return response.data;
        }
        if (isGuestMode()) {
          const result = updateGuestSchedule(shioriId, lineNon, scheduleData);
          if (options.refresh !== false) {
            this.schedules = fetchGuestSchedules(shioriId);
          }
          return result;
        }

        const response = await axios.put(
          `${API_URL}/schedule/${shioriId}/${lineNon}`,
          scheduleData,
          withShioriAccessContext(shioriId),
        );
        if (options.refresh !== false && !shareToken) {
          await this.fetchSchedules(shioriId, { silent: options.silent });
        }
        return response.data;
      } catch (err) {
        if (!options.silent) {
          this.error = err;
        }
        console.error("スケジュール更新エラー:", err);
        throw err;
      } finally {
        if (!options.silent) {
          this.isLoading = false;
        }
      }
    },
    async deleteSchedule(shioriId, lineNon, shareToken = null) {
      this.isLoading = true;
      this.error = null;
      try {
        if (shareToken) {
          const response = await axios.delete(
            `${API_URL}/shiori/shared/${shareToken}/schedule/${lineNon}`,
          );
          return response.data;
        }
        if (isGuestMode()) {
          const result = deleteGuestSchedule(shioriId, lineNon);
          this.schedules = fetchGuestSchedules(shioriId);
          return result;
        }

        const response = await axios.delete(
          `${API_URL}/schedule/${shioriId}/${lineNon}`,
          withShioriAccessContext(shioriId),
        );
        // 削除成功後、非共有の通常モードのみリストを再取得
        if (!shareToken) {
          await this.fetchSchedules(shioriId);
        }
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("スケジュール削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    clearSchedules() {
      this.schedules = [];
      this.error = null;
    },
  },
});
